import { useState } from 'react';
import { HIRE_PAGE_CONTAINER, HIRE_SECTION_SPACING } from '@/lib/hireResponsive';
import { AppLayout } from '@/components/AppLayout';
import { ServicePageGuard } from '@/components/ServicePageGuard';
import { SERVICE_KEYS } from '@/lib/serviceKeys';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Users, Shield, ScrollText } from 'lucide-react';
import { UsersList } from '@/components/team/UsersList';
import { RoleDetails } from '@/components/team/RoleDetails';
import { AuditLogView } from '@/components/team/AuditLogView';
import { usePermissions } from '@/hooks/usePermissions';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function TeamManagement() {
  const { t } = useTranslation();
  const { can, isLoading } = usePermissions();
  const [tab, setTab] = useState('users');

  if (!isLoading && !can('users', 'view')) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <AppLayout>
      <ServicePageGuard serviceKey={SERVICE_KEYS.TEAM_MANAGEMENT}>
      <div className={`${HIRE_SECTION_SPACING} ${HIRE_PAGE_CONTAINER}`}>
        <Card className="shadow-card border-border border-t-4 border-t-primary overflow-hidden">
          <div className="p-5 sm:p-6">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold font-display text-foreground">
              {t('hire.team.title')}
            </h1>
            <p className="text-muted-foreground mt-1.5 text-sm">
              {t('hire.team.subtitle')}
            </p>
          </div>
        </Card>

        <Tabs value={tab} onValueChange={setTab} className="space-y-5">
          <TabsList className="rounded-xl">
            <TabsTrigger value="users" className="gap-1.5 rounded-lg touch-target">
              <Users className="w-4 h-4" /> {t('hire.team.users')}
            </TabsTrigger>
            <TabsTrigger value="roles" className="gap-1.5 rounded-lg touch-target">
              <Shield className="w-4 h-4" /> {t('hire.team.roles')}
            </TabsTrigger>
            {can('audit_logs', 'view') && (
              <TabsTrigger value="audit" className="gap-1.5 rounded-lg touch-target">
                <ScrollText className="w-4 h-4" /> {t('hire.team.auditLog')}
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="users">
            <UsersList />
          </TabsContent>

          <TabsContent value="roles">
            <RoleDetails />
          </TabsContent>

          {can('audit_logs', 'view') && (
            <TabsContent value="audit">
              <AuditLogView />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </ServicePageGuard>
    </AppLayout>
  );
}